import { addUFOCustomData } from '@atlaskit/react-ufo/add-ufo-custom-data';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

export const QUERY_LAYER_KEYS = {
	/** The results query that gates vc90. */
	asyncQuery: {
		dispatchAt: 'asyncQueryDispatchAt',
		dataAvailableAt: 'asyncQueryDataAvailableAt',
		renderedAt: 'asyncQueryRenderedAt',
	},
} as const;

/** An operation whose query layer is measured. */
export type QueryLayerOperation = keyof typeof QUERY_LAYER_KEYS;

/** The results query that gates vc90 — shared between the dispatch and data-available call sites. */
export const ASYNC_QUERY: QueryLayerOperation = 'asyncQuery';

/**
 * Records a UFO custom-data mark as an offset (ms) from the active
 * interaction's start. Exported so other call sites (e.g. preload timings)
 * can record marks on the same timeline without duplicating the arithmetic.
 */
export function recordInteractionOffset(key: string, at: number): void {
	// No live interaction means nothing to anchor the offset to. `addUFOCustomData`
	// would no-op anyway; returning early keeps the arithmetic out of that case.
	const interactionStart = getActiveInteraction()?.start;
	if (typeof interactionStart !== 'number') {
		return;
	}

	addUFOCustomData({ [key]: Math.round(at - interactionStart) });
}

// Operations with a dispatch outstanding, i.e. one that hasn't yet had a matching
// `recordQueryDataAvailable` call. Consumed by the first qualifying call, so a
// render triggered by something other than the dispatch itself (e.g. loading the
// next page of an already-resolved connection) cannot push the boundary out.
const pendingDispatches = new Set<QueryLayerOperation>();

export function recordQueryDispatch(
	operation: QueryLayerOperation,
	at: number,
	{ cacheAvailable = false }: { cacheAvailable?: boolean } = {},
): void {
	recordInteractionOffset(QUERY_LAYER_KEYS[operation].dispatchAt, at);

	if (cacheAvailable) {
		// The store already satisfies the query, so `loadQuery` issues no request
		// under `store-or-network` — the query layer is zero-width by definition.
		// Recording data-available here, rather than waiting for the render/commit
		// that follows, keeps that later (purely render-cost) instant from being
		// reported as query time.
		recordInteractionOffset(QUERY_LAYER_KEYS[operation].dataAvailableAt, at);
		return;
	}

	pendingDispatches.add(operation);
}

/**
 * Records the data-available position for the dispatch it bounds.
 *
 * Returns whether it actually recorded anything. A call site keys this off `edges`
 * (or similar render output), which also changes for reasons unrelated to the
 * dispatch itself — e.g. pagination growing an already-resolved connection. This
 * return value makes that conditional explicit at the call site instead of letting
 * it read as an unconditional side effect; the correctness itself still comes from
 * `pendingDispatches` above, not from the caller checking this value.
 */
export function recordQueryDataAvailable(operation: QueryLayerOperation, at: number): boolean {
	if (!pendingDispatches.delete(operation)) {
		return false;
	}

	recordInteractionOffset(QUERY_LAYER_KEYS[operation].dataAvailableAt, at);
	return true;
}

/**
 * Records the point at which the browser actually painted the results, so downstream
 * analysis can split `vc90 - asyncQueryDataAvailableAt` (today one undifferentiated bucket)
 * into render cost (`asyncQueryRenderedAt - asyncQueryDataAvailableAt`) and whatever happens
 * on the page after render (`vc90 - asyncQueryRenderedAt`). Callers are expected to schedule
 * this via `scheduleOnPaint` themselves and pass its own re-entrancy guard against
 * `recordQueryDataAvailable`'s return value; unlike that function, this one has no
 * `pendingDispatches`-style guard of its own.
 */
export function recordResultsRendered(operation: QueryLayerOperation, at: number): void {
	recordInteractionOffset(QUERY_LAYER_KEYS[operation].renderedAt, at);
}
